/** @format */

import {useMemo, useCallback} from 'react';

import {add, format} from 'date-fns';

import {useCompanyContext} from 'contexts/CompanyContext';
import {OauthProvider} from 'graphql/generated/types';

import {useGetAiReportsPricingQuery} from 'graphql/__generated__';

import {
  useCreateMarvinAiReportMutation,
  useGetAiReportsQuery,
  useGetCurrentUserQuery,
  useGetCurrentUserReportsLimitQuery,
} from '../graphql/__generated__';
import {
  transformGoalsToOptions,
  transformCampaignsToOptions,
  resolveGoalsWithDetails,
} from '../components/Form/helpers';
import {transformAiReportsToTableData} from '../components/BriefReports/helpers/transformData';
import {FormDataOptions, FormState} from '../types';

interface UseAiModuleDataParams {
  platforms: OauthProvider[];
}

export const useAiModuleData = ({platforms}: UseAiModuleDataParams) => {
  const {
    currentCompany: {id: companyId, uuid: companyUuid, name: companyName},
  } = useCompanyContext();

  const now = new Date();
  const startDate = add(now, {days: -7});

  let needToConnectMetrika = !platforms.includes('METRIKA');

  const {
    loading,
    error,
    data,
    refetch: refetchCurrentUser,
  } = useGetCurrentUserQuery({
    variables: {
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(now, 'yyyy-MM-dd'),
      companyId: companyId,
    },
    skip: needToConnectMetrika,
  });

  if (error && !data?.currentUser?.company) {
    needToConnectMetrika = true;
  }

  const {
    data: aiReportsData,
    loading: aiReportsLoading,
    error: aiReportsError,
    refetch: refetchAiReports,
  } = useGetAiReportsQuery({});

  const {
    data: reportsLimitData,
    loading: reportsLimitLoading,
    error: reportsLimitError,
    refetch: refetchReportsLimit,
  } = useGetCurrentUserReportsLimitQuery({
    skip: needToConnectMetrika,
  });

  const [generateReport, {loading: generateReportLoading}] = useCreateMarvinAiReportMutation();

  const {data: pricingData, loading: pricingLoading} = useGetAiReportsPricingQuery();
  const unitPrice = pricingData?.currentUser?.subscription.tariffDetails?.aiReportsUnitPrice ?? 0;

  const formDataOptions: FormDataOptions = useMemo(() => {
    const goalOptions = transformGoalsToOptions(data?.currentUser?.company?.analyticGoals);
    const campaignOptions = transformCampaignsToOptions(
      data?.currentUser?.currentCompany?.campaignMediaplanStats,
    );

    return {
      goalOptions,
      campaignOptions,
    };
  }, [data]);

  const {manualReports, monitoringReports} = useMemo(() => {
    const allReports = transformAiReportsToTableData(
      aiReportsData?.currentUser?.currentCompany?.marvinAiReports,
    );

    return {
      manualReports: allReports.filter(r => r.source === 'MANUAL'),
      monitoringReports: allReports.filter(r => r.source === 'MONITORING'),
    };
  }, [aiReportsData]);

  const monitoringConfig = data?.currentUser?.currentCompany?.aiMonitoringConfig ?? null;

  const remainingReports = reportsLimitData?.currentUser?.aiReportsLimit.remainingReports ?? 0;

  const purchaseState: 'has_balance' | 'exhausted' | 'never_purchased' =
    remainingReports > 0
      ? 'has_balance'
      : manualReports.length > 0
        ? 'exhausted'
        : 'never_purchased';

  const handleFormSubmit = async (formData: FormState) => {
    try {
      const {goals, missingGoalIds} = resolveGoalsWithDetails(
        formData.goals,
        formDataOptions.goalOptions,
      );

      if (missingGoalIds.length > 0) {
        console.warn('useAiModuleData: Some goal IDs could not be resolved and were dropped.', {
          missingGoalIds,
        });
      }

      await generateReport({
        variables: {
          data: {
            periodFrom: format(formData.startDate as Date, 'yyyy-MM-dd'),
            periodTo: format(formData.endDate as Date, 'yyyy-MM-dd'),
            companyUuid: companyUuid,
            attributionModel: formData.attributionModel,
            goals,
            campaigns: formData.campaigns.map(campaignId => Number(campaignId)),
            totalBudget: formData.budget,
            kpi: formData.kpi,
            kpiPlan: formData.kpiAmount,
            companyId,
            projectName: companyName,
          },
        },
      });
      await refetchAiReports();
      await refetchReportsLimit();
      return {success: true};
    } catch (error) {
      await refetchReportsLimit();
      return {success: false, error};
    }
  };

  const refetchData = useCallback(async () => {
    await Promise.all([refetchCurrentUser(), refetchAiReports()]);
  }, [refetchCurrentUser, refetchAiReports]);

  return {
    // Loading states
    loading,
    aiReportsLoading,
    reportsLimitLoading,
    generateReportLoading,
    pricingLoading,

    // Errors
    error,
    aiReportsError,
    reportsLimitError,

    // Data
    formDataOptions,
    manualReports,
    monitoringReports,
    remainingReports,
    monitoringConfig,
    purchaseState,
    unitPrice,

    // Functions
    handleFormSubmit,
    refetchData,

    needToConnectMetrika,
  };
};
